import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Pencil, Trash2, FileDown, Search, Loader2, RefreshCw } from 'lucide-react';
import dayjs from 'dayjs';
import {
  MisReport,
  MisReportAsset,
  MisReportPayload,
  MisReportSortField,
  MisReportsApi,
} from '@/services/mis-reports.api';
import { SchoolsApi } from '@/services/superadmin.api';
import { OrgPortalApi } from '@/services/org.api';
import { AcademicYearsApi } from '@/services/academic.api';
import { ExpensesApi } from '@/services/expenses.api';
import { IncomesApi } from '@/services/incomes.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Field, Input, Select, Checkbox, Textarea } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';

const schema = z.object({
  schoolId: z.string().optional(),
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Required'),
  districtName: z.string().optional(),
  q1ProperDocuments: z.boolean(),
  q1Details: z.string().optional(),
  q2FeeReceivable: z.coerce.number().optional(),
  q3AmountPayable: z.coerce.number().optional(),
  q5PreviousYearFees: z.coerce.number().optional(),
  q6PendingPaymentHo: z.coerce.number().optional(),
});
type FormValues = z.infer<typeof schema>;

// Predefined line-item labels — matches the old system's own fixed form
// fields exactly (confirmed against the legacy `mis_reports` data). Every
// report shows all of these, even at 0; no "add line item" affordance.
const INCOME_LABELS = [
  'Admission Fee',
  'Amount from outside',
  'Books sale',
  'ID Card',
  'income from outside',
  'Opening Cash & Bank',
  'Pre-school box',
  'Term fee',
  'Uniform sale',
];

const EXPENSE_LABELS = [
  'Building Tax',
  'Cash paid to HO',
  'Electricity charge',
  'Internet & Telephone',
  'Marketing &Advertisement Exp',
  'Noorani Salary',
  'Printing and Stationery',
  'Rent',
  'Repairs & Maintenance',
  'Salary and allowance',
  'T.A',
  'Travel Expenses',
];

/**
 * Predefined labels always come first (defaulting to 0). Any EXTRA keys an
 * existing record already has (a few older migrated reports used one-off
 * labels like "Tea"/"Home Schooling" the old form didn't have as a fixed
 * field) are preserved and appended after, so editing a report never
 * silently drops that historical data — there's just no button to add a
 * new one going forward.
 */
function mergeWithPredefined(
  existing: Record<string, number> | null | undefined,
  predefined: string[],
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const label of predefined) out[label] = Number(existing?.[label]) || 0;
  for (const [label, amount] of Object.entries(existing ?? {})) {
    if (!predefined.includes(label)) out[label] = Number(amount) || 0;
  }
  return out;
}

function money(v: string | number): string {
  return Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Finds the tenant academic year whose date range contains the 1st of the given "YYYY-MM" month — the pre-fill bridge needs an academicYearId, but MIS Report months are plain calendar months with no academic-year concept of their own. */
function academicYearForMonth(years: { id: string; startDate: string; endDate: string }[], monthStr: string): string | undefined {
  const target = `${monthStr}-01`;
  return years.find((y) => target >= y.startDate.slice(0, 10) && target <= y.endDate.slice(0, 10))?.id;
}

const columnHelper = createColumnHelper<MisReport>();

export function MisReportsPage({ scope }: { scope: 'superadmin' | 'organization' | 'school' }) {
  const qc = useQueryClient();
  // Both Super Admin and an Org Admin see a multi-school view (school
  // column, school filter, school picker in the create form) — only a
  // school-admin session is pinned to its own one school.
  const showSchoolPicker = scope !== 'school';
  const [modal, setModal] = useState<{ open: boolean; report?: MisReport }>({ open: false });
  const [confirm, setConfirm] = useState<{ open: boolean; report?: MisReport }>({ open: false });
  const [schoolId, setSchoolId] = useState('');
  const [pdfBusyId, setPdfBusyId] = useState<string | null>(null);
  const [errorNotice, setErrorNotice] = useState<string | null>(null);

  const dt = useDataTable({ defaultLimit: 20 });

  useEffect(() => {
    dt.onPaginationChange((prev) => ({ ...prev, pageIndex: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId]);

  // An Org Admin can only ever list THEIR OWN org's schools (SchoolsApi is
  // Super-Admin-only and would 403) — different source, same shape.
  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-mis-filter', scope],
    queryFn: async () =>
      scope === 'organization' ? (await OrgPortalApi.listSchools({ limit: 100 })).items : (await SchoolsApi.list({ limit: 100 })).items,
    enabled: showSchoolPicker,
  });

  const { data, isFetching } = useQuery({
    queryKey: ['mis-reports', { ...dt.params, schoolId, scope }],
    queryFn: () =>
      MisReportsApi.list({
        ...dt.params,
        sortBy: dt.params.sortBy as MisReportSortField | undefined,
        schoolId: showSchoolPicker ? schoolId || undefined : undefined,
      }),
    placeholderData: keepPreviousData,
  });

  const upsert = useMutation({
    mutationFn: (vals: { id?: string; payload: MisReportPayload }) =>
      vals.id ? MisReportsApi.update(vals.id, vals.payload) : MisReportsApi.create(vals.payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mis-reports'] });
      setModal({ open: false });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => MisReportsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mis-reports'] });
      setConfirm({ open: false });
    },
  });

  const downloadPdf = async (report: MisReport) => {
    setPdfBusyId(report.id);
    setErrorNotice(null);
    try {
      const { pdfFilePath } = await MisReportsApi.pdf(report.id);
      window.open(pdfFilePath, '_blank');
    } catch (e) {
      setErrorNotice(errMsg(e) ?? 'Could not generate PDF');
    } finally {
      setPdfBusyId(null);
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('month', {
        id: 'month',
        header: 'Period',
        enableSorting: true,
        cell: (info) => (
          <div className="leading-tight">
            <div className="font-medium text-slate-900">{info.getValue()}</div>
            {showSchoolPicker && (
              <div className="text-xs text-slate-500">{info.row.original.school?.name}</div>
            )}
          </div>
        ),
      }),
      columnHelper.accessor('totalIncome', {
        id: 'totalIncome',
        header: 'Total Income',
        enableSorting: true,
        cell: (info) => <span className="tabular-nums">{money(info.getValue())}</span>,
      }),
      columnHelper.accessor('totalExpense', {
        id: 'totalExpense',
        header: 'Total Expense',
        enableSorting: true,
        cell: (info) => <span className="tabular-nums">{money(info.getValue())}</span>,
      }),
      columnHelper.accessor('closingCashBank', {
        id: 'closingCashBank',
        header: 'Closing Cash & Bank',
        enableSorting: false,
        cell: (info) => {
          const v = Number(info.getValue());
          return (
            <span className={`tabular-nums font-medium ${v < 0 ? 'text-red-600' : 'text-slate-900'}`}>
              {money(v)}
            </span>
          );
        },
      }),
      columnHelper.accessor('createdAt', {
        id: 'createdAt',
        header: 'Created At',
        enableSorting: true,
        cell: (info) => dayjs(info.getValue()).format('DD MMM YYYY'),
      }),
      columnHelper.display({
        id: 'createdByName',
        header: 'Created By',
        enableSorting: false,
        cell: (info) => info.row.original.createdByName ?? '—',
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Action',
        enableSorting: false,
        cell: (info) => {
          const r = info.row.original;
          return (
            <div className="inline-flex items-center gap-1">
              <button
                className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-600"
                onClick={() => setModal({ open: true, report: r })}
                title="Edit"
              >
                <Pencil className="h-4 w-4" />
              </button>
              <button
                className="rounded-md p-1.5 text-slate-500 hover:bg-indigo-50 hover:text-indigo-600 disabled:opacity-50"
                onClick={() => downloadPdf(r)}
                disabled={pdfBusyId === r.id}
                title="Download PDF"
              >
                {pdfBusyId === r.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileDown className="h-4 w-4" />
                )}
              </button>
              <button
                className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"
                onClick={() => setConfirm({ open: true, report: r })}
                title="Delete"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          );
        },
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pdfBusyId, showSchoolPicker],
  );

  return (
    <>
      <PageHeader
        title="MIS Reports"
        description="Monthly financial reporting — Head Office oversight."
        actions={
          <button className="btn-primary" onClick={() => setModal({ open: true })}>
            <Plus className="mr-1.5 h-4 w-4" /> Create New Report
          </button>
        }
      />

      {errorNotice && (
        <div className="mb-4 flex items-center justify-between rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          <span>{errorNotice}</span>
          <button onClick={() => setErrorNotice(null)} className="text-red-600 hover:text-red-800">
            ✕
          </button>
        </div>
      )}

      <DataTable
        columns={columns}
        result={data}
        state={dt}
        loading={isFetching}
        emptyMessage="No MIS reports yet."
        toolbar={
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-xs">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={dt.searchInput}
                onChange={(e) => dt.setSearchInput(e.target.value)}
                placeholder="Search by month (YYYY-MM)…"
                className="pl-9"
              />
            </div>
            {showSchoolPicker && (
              <Select value={schoolId} onChange={(e) => setSchoolId(e.target.value)} className="sm:w-56">
                <option value="">All schools</option>
                {schools.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            )}
          </div>
        }
      />

      <MisReportFormModal
        open={modal.open}
        report={modal.report}
        scope={scope}
        showSchoolPicker={showSchoolPicker}
        schools={schools}
        onClose={() => setModal({ open: false })}
        saving={upsert.isPending}
        errorMsg={errMsg(upsert.error)}
        onSubmit={(payload) => upsert.mutate({ id: modal.report?.id, payload })}
      />

      <ConfirmDialog
        open={confirm.open}
        onClose={() => setConfirm({ open: false })}
        onConfirm={() => confirm.report && remove.mutate(confirm.report.id)}
        loading={remove.isPending}
        title="Delete MIS report?"
        message={`This permanently deletes the ${confirm.report?.month} report${
          showSchoolPicker && confirm.report?.school ? ` for ${confirm.report.school.name}` : ''
        }.`}
        confirmText="Delete report"
      />
    </>
  );
}

function MisReportFormModal({
  open,
  report,
  scope,
  showSchoolPicker,
  schools,
  onClose,
  onSubmit,
  saving,
  errorMsg,
}: {
  open: boolean;
  report?: MisReport;
  scope: 'superadmin' | 'organization' | 'school';
  showSchoolPicker: boolean;
  schools: { id: string; name: string }[];
  onClose: () => void;
  onSubmit: (payload: MisReportPayload) => void;
  saving: boolean;
  errorMsg?: string;
}) {
  const qc = useQueryClient();
  const isEdit = !!report;
  const [incomeData, setIncomeData] = useState<Record<string, number>>(() =>
    mergeWithPredefined(undefined, INCOME_LABELS),
  );
  const [expenseData, setExpenseData] = useState<Record<string, number>>(() =>
    mergeWithPredefined(undefined, EXPENSE_LABELS),
  );
  const [assets, setAssets] = useState<MisReportAsset[]>([]);
  const [prefilling, setPrefilling] = useState(false);
  const prefilledMonthRef = useRef<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: {
      schoolId: report?.schoolId ?? '',
      month: report?.month ?? '',
      districtName: report?.districtName ?? '',
      q1ProperDocuments: report?.q1ProperDocuments ?? false,
      q1Details: report?.q1Details ?? '',
      q2FeeReceivable: report?.q2FeeReceivable != null ? Number(report.q2FeeReceivable) : undefined,
      q3AmountPayable: report?.q3AmountPayable != null ? Number(report.q3AmountPayable) : undefined,
      q5PreviousYearFees: report?.q5PreviousYearFees != null ? Number(report.q5PreviousYearFees) : undefined,
      q6PendingPaymentHo: report?.q6PendingPaymentHo != null ? Number(report.q6PendingPaymentHo) : undefined,
    },
  });

  useEffect(() => {
    if (open) {
      setIncomeData(mergeWithPredefined(report?.incomeData, INCOME_LABELS));
      setExpenseData(mergeWithPredefined(report?.expenseData, EXPENSE_LABELS));
      setAssets(report?.q4AssetsPurchased ?? []);
      prefilledMonthRef.current = null;
    }
  }, [open, report]);

  // Pre-fill bridge (Option A): only viable for a real school-tier session
  // creating its OWN report — a superadmin/org-admin picks an arbitrary
  // OTHER school here, and the /school/* summary endpoints only ever read
  // the CALLER's own tenant schema, so there's no school to pre-fill from
  // until that session actually becomes one. Only fires on CREATE — an
  // existing report's saved data is never silently overwritten by opening
  // the edit modal; "Regenerate" below is the explicit, deliberate action
  // for that.
  const canPrefill = open && !isEdit && scope === 'school';
  const { data: yearsForPrefill = [] } = useQuery({
    queryKey: ['academic-years', 'for-mis-prefill'],
    queryFn: () => AcademicYearsApi.list({ limit: 100 }).then((p) => p.items),
    enabled: canPrefill,
  });
  const monthValue = watch('month');
  useEffect(() => {
    if (!canPrefill || !monthValue || !/^\d{4}-(0[1-9]|1[0-2])$/.test(monthValue)) return;
    if (prefilledMonthRef.current === monthValue) return;
    const academicYearId = academicYearForMonth(yearsForPrefill, monthValue);
    if (!academicYearId) return;
    prefilledMonthRef.current = monthValue;
    const month = Number(monthValue.slice(5, 7));
    setPrefilling(true);
    Promise.all([ExpensesApi.monthlySummary(academicYearId, month), IncomesApi.monthlySummary(academicYearId, month)])
      .then(([expenseSummary, incomeSummary]) => {
        setExpenseData(mergeWithPredefined(expenseSummary, EXPENSE_LABELS));
        setIncomeData(mergeWithPredefined(incomeSummary, INCOME_LABELS));
      })
      .finally(() => setPrefilling(false));
  }, [canPrefill, monthValue, yearsForPrefill]);

  const regenerateMut = useMutation({
    mutationFn: () => MisReportsApi.regenerateData(report!.id),
    onSuccess: (updated) => {
      setIncomeData(mergeWithPredefined(updated.incomeData, INCOME_LABELS));
      setExpenseData(mergeWithPredefined(updated.expenseData, EXPENSE_LABELS));
      qc.invalidateQueries({ queryKey: ['mis-reports'] });
    },
  });

  const totalIncome = Object.values(incomeData).reduce((s, v) => s + (Number(v) || 0), 0);
  const totalExpense = Object.values(expenseData).reduce((s, v) => s + (Number(v) || 0), 0);
  const closing = totalIncome - totalExpense;

  const submit = (vals: FormValues) => {
    onSubmit({
      schoolId: showSchoolPicker && !isEdit ? vals.schoolId : undefined,
      month: !isEdit ? vals.month : undefined,
      districtName: vals.districtName || undefined,
      incomeData,
      expenseData,
      q1ProperDocuments: vals.q1ProperDocuments,
      q1Details: vals.q1Details || undefined,
      q2FeeReceivable: vals.q2FeeReceivable,
      q3AmountPayable: vals.q3AmountPayable,
      q4AssetsPurchased: assets.filter((a) => a.item.trim()),
      q5PreviousYearFees: vals.q5PreviousYearFees,
      q6PendingPaymentHo: vals.q6PendingPaymentHo,
    });
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title={report ? `Edit ${report.month} report` : 'Create MIS Report'}
      description="Line items auto-total into Closing Cash & Bank — computed server-side on save."
      size="xl"
      footer={
        <>
          {isEdit && report?.status === 'draft' && (
            <button
              type="button"
              className="btn-secondary mr-auto"
              onClick={() => regenerateMut.mutate()}
              disabled={regenerateMut.isPending}
              title="Re-pull fresh income/expense sums from the ledger for this month"
            >
              <RefreshCw className={`mr-1.5 h-4 w-4 ${regenerateMut.isPending ? 'animate-spin' : ''}`} />
              {regenerateMut.isPending ? 'Regenerating…' : 'Regenerate'}
            </button>
          )}
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={handleSubmit(submit)} disabled={saving}>
            {saving ? 'Saving…' : report ? 'Save changes' : 'Create report'}
          </button>
        </>
      }
    >
      <form onSubmit={handleSubmit(submit)} className="space-y-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {showSchoolPicker && !isEdit && (
            <Field label="School" required error={errors.schoolId?.message} className="sm:col-span-1">
              <Select {...register('schoolId')}>
                <option value="">— Select —</option>
                {schools.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          <Field label="Month" required error={errors.month?.message}>
            <Input type="month" {...register('month')} disabled={isEdit} />
          </Field>
          <Field label="District" error={errors.districtName?.message}>
            <Input {...register('districtName')} placeholder="e.g. Kannur" />
          </Field>
        </div>

        {prefilling && (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Pre-filling from this month's expenses/income — you can still edit any value below.
          </div>
        )}
        <PredefinedLineItemSection title="Income Details" data={incomeData} setData={setIncomeData} />
        <PredefinedLineItemSection title="Expense Details" data={expenseData} setData={setExpenseData} />

        <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-4 py-3">
          <div className="text-sm text-slate-600">
            Total Income <span className="font-medium text-slate-900">{money(totalIncome)}</span> &nbsp;·&nbsp;
            Total Expense <span className="font-medium text-slate-900">{money(totalExpense)}</span>
          </div>
          <div className={`text-lg font-bold ${closing < 0 ? 'text-red-600' : 'text-slate-900'}`}>
            Closing Cash &amp; Bank: {money(closing)}
          </div>
        </div>

        <div className="space-y-4 border-t border-slate-200 pt-4">
          <h4 className="font-medium text-slate-900">MIS Report Questions</h4>

          <Field label="Q1. Proper documents maintained?" error={errors.q1ProperDocuments?.message}>
            <Checkbox label="Yes, documents are properly maintained" {...register('q1ProperDocuments')} />
          </Field>
          <Field label="Q1 details (optional)" error={errors.q1Details?.message}>
            <Textarea rows={2} {...register('q1Details')} />
          </Field>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Q2. Fee receivable" error={errors.q2FeeReceivable?.message}>
              <Input type="number" step="0.01" {...register('q2FeeReceivable')} />
            </Field>
            <Field label="Q3. Amount payable" error={errors.q3AmountPayable?.message}>
              <Input type="number" step="0.01" {...register('q3AmountPayable')} />
            </Field>
          </div>

          <div>
            <span className="mb-1 block text-sm font-medium text-slate-700">Q4. Assets purchased</span>
            <div className="space-y-2">
              {assets.map((a, idx) => (
                <div key={idx} className="flex gap-2">
                  <Input
                    placeholder="Item"
                    value={a.item}
                    onChange={(e) =>
                      setAssets((prev) => prev.map((p, i) => (i === idx ? { ...p, item: e.target.value } : p)))
                    }
                  />
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Cost"
                    className="w-40"
                    value={a.cost}
                    onChange={(e) =>
                      setAssets((prev) =>
                        prev.map((p, i) => (i === idx ? { ...p, cost: Number(e.target.value) || 0 } : p)),
                      )
                    }
                  />
                  <button
                    type="button"
                    className="rounded-md p-2 text-slate-400 hover:bg-red-50 hover:text-red-600"
                    onClick={() => setAssets((prev) => prev.filter((_, i) => i !== idx))}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="btn-secondary text-xs"
                onClick={() => setAssets((prev) => [...prev, { item: '', cost: 0 }])}
              >
                <Plus className="mr-1 h-3.5 w-3.5" /> Add asset
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Q5. Previous year fees collected" error={errors.q5PreviousYearFees?.message}>
              <Input type="number" step="0.01" {...register('q5PreviousYearFees')} />
            </Field>
            <Field label="Q6. Pending payment to Head Office" error={errors.q6PendingPaymentHo?.message}>
              <Input type="number" step="0.01" {...register('q6PendingPaymentHo')} />
            </Field>
          </div>
        </div>

        {errorMsg && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errorMsg}</div>}
      </form>
    </Modal>
  );
}

function PredefinedLineItemSection({
  title,
  data,
  setData,
}: {
  title: string;
  data: Record<string, number>;
  setData: React.Dispatch<React.SetStateAction<Record<string, number>>>;
}) {
  const total = Object.values(data).reduce((s, v) => s + (Number(v) || 0), 0);
  return (
    <div className="overflow-hidden rounded-md border border-slate-200">
      <div className="border-b border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-semibold text-slate-900">
        {title}
      </div>
      <div className="divide-y divide-slate-100">
        {Object.entries(data).map(([label, amount]) => (
          <div key={label} className="flex items-center justify-between gap-4 px-4 py-2.5">
            <span className="text-sm text-slate-700">{label}</span>
            <Input
              type="number"
              step="0.01"
              className="w-48 bg-slate-100"
              value={amount}
              onChange={(e) => setData((prev) => ({ ...prev, [label]: Number(e.target.value) || 0 }))}
            />
          </div>
        ))}
      </div>
      <div className="border-t border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-900">
        Total {title.replace(' Details', '')}: {money(total)}
      </div>
    </div>
  );
}

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}
